import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { ArrowLeft, CheckCircle2, ExternalLink, GraduationCap } from 'lucide-react';
import { KbAttachmentsCard } from '@/components/knowledge/KbAttachments';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { FormattedText } from '@/components/ui/FormattedText';
import { toast } from '@/store/toastStore';
import {
  acknowledgeMyKbAssignment,
  getKbQuiz,
  getKnowledgeBaseDocument,
  kbQueryKeys,
  listKbQuizzes,
  startKbQuizAttempt,
  submitKbQuizAttempt,
} from '@/services/knowledge-base.service';

export function KnowledgeArticlePage() {
  const { id } = useParams<{ id: string }>();
  const documentId = Number(id);
  const [searchParams] = useSearchParams();
  const assignmentIdParam = searchParams.get('assignmentId');
  const assignmentId = assignmentIdParam ? Number(assignmentIdParam) : undefined;
  const queryClient = useQueryClient();

  const docQuery = useQuery({
    queryKey: kbQueryKeys.document(documentId),
    queryFn: () => getKnowledgeBaseDocument(documentId),
    enabled: documentId > 0,
  });

  const quizListQuery = useQuery({
    queryKey: ['kb', 'quizzes', { targetType: 'document', targetId: documentId }],
    queryFn: () => listKbQuizzes({ targetType: 'document', targetId: documentId }),
    enabled: documentId > 0,
  });

  const activeQuizId = quizListQuery.data?.find((q) => q.status === 'active')?.kbQuizId;

  const ackMutation = useMutation({
    mutationFn: () => acknowledgeMyKbAssignment(assignmentId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.myAssignments() });
      toast.success('Marked as read');
    },
    onError: () => toast.error('Failed to mark as read'),
  });

  if (docQuery.isLoading) {
    return <div className="p-6 text-sm text-slate-500">Loading...</div>;
  }

  const doc = docQuery.data;
  if (!doc) {
    return <div className="p-6">Article not found.</div>;
  }

  return (
    <div className="p-6 space-y-4">
      <Link to="/knowledge" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-4 w-4" />
        Back to my training
      </Link>
      <PageHeader
        title={doc.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {doc.category && <Badge variant="neutral">{doc.category}</Badge>}
            {doc.tags.map((t) => (
              <span key={t} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                {t}
              </span>
            ))}
          </span>
        }
        actions={
          doc.url ? (
            <a
              href={doc.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <ExternalLink className="h-4 w-4" />
              Open original
            </a>
          ) : null
        }
      />
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        {doc.body ? (
          doc.bodyFormat === 'html' ? (
            <div
              className="prose prose-sm max-w-none text-slate-800 [&_img]:max-w-full [&_img]:rounded-md"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(doc.body) }}
            />
          ) : (
            <FormattedText text={doc.body} className="prose prose-sm max-w-none text-slate-800" />
          )
        ) : (
          <p className="text-sm italic text-slate-400">This article has no body content.</p>
        )}
      </div>

      <KbAttachmentsCard attachments={doc.attachments ?? []} />

      {activeQuizId ? (
        <EmbeddedQuiz quizId={activeQuizId} assignmentId={assignmentId} />
      ) : assignmentId ? (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-sm text-slate-700">
            When you've finished reading, mark this training as complete.
          </p>
          <Button
            className="mt-3"
            leftIcon={<CheckCircle2 className="h-4 w-4" />}
            onClick={() => ackMutation.mutate()}
            loading={ackMutation.isPending}
          >
            Mark as Read
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function EmbeddedQuiz({ quizId, assignmentId }: { quizId: number; assignmentId?: number }) {
  const queryClient = useQueryClient();
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Record<number, number[]>>({});
  const [result, setResult] = useState<{ scorePct: number; passed: boolean; passThreshold: number } | null>(null);

  const quizQuery = useQuery({
    queryKey: kbQueryKeys.quiz(quizId),
    queryFn: () => getKbQuiz(quizId),
  });

  const startMutation = useMutation({
    mutationFn: () => startKbQuizAttempt(quizId, { assignmentId: assignmentId ?? null }),
    onSuccess: (resp) => setAttemptId(resp.kbQuizAttemptId),
    onError: () => toast.error('Failed to start quiz'),
  });

  const submitMutation = useMutation({
    mutationFn: () =>
      submitKbQuizAttempt(
        attemptId!,
        Object.entries(answers).map(([questionId, selectedChoiceIds]) => ({
          questionId: Number(questionId),
          selectedChoiceIds,
        })),
      ),
    onSuccess: (resp) => {
      setResult({
        scorePct: resp.scorePct,
        passed: resp.passed,
        passThreshold: resp.passThreshold,
      });
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.myAssignments() });
      if (resp.passed) toast.success(`Passed with ${resp.scorePct}%`);
      else toast.error(`Did not pass — got ${resp.scorePct}%`);
    },
    onError: () => toast.error('Could not submit quiz'),
  });

  const allAnswered = useMemo(() => {
    if (!quizQuery.data) return false;
    return quizQuery.data.questions.every((q) => (answers[q.kbQuizQuestionId]?.length ?? 0) > 0);
  }, [quizQuery.data, answers]);

  function toggleAnswer(questionId: number, choiceId: number, multi: boolean) {
    setAnswers((curr) => {
      const existing = curr[questionId] ?? [];
      if (multi) {
        return existing.includes(choiceId)
          ? { ...curr, [questionId]: existing.filter((id) => id !== choiceId) }
          : { ...curr, [questionId]: [...existing, choiceId] };
      }
      return { ...curr, [questionId]: [choiceId] };
    });
  }

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4">
      <div className="flex items-center gap-2">
        <GraduationCap className="h-5 w-5 text-blue-600" />
        <h3 className="text-base font-semibold text-slate-900">{quizQuery.data?.title ?? 'Quiz'}</h3>
      </div>

      {!attemptId && !result && (
        <>
          <p className="mt-1 text-sm text-slate-600">
            Pass with {quizQuery.data?.passThreshold ?? 80}% or higher to mark this training complete.
          </p>
          <Button className="mt-3" onClick={() => startMutation.mutate()} loading={startMutation.isPending}>
            Start Quiz
          </Button>
        </>
      )}

      {attemptId && !result && quizQuery.data && (
        <div className="mt-4 space-y-4">
          {quizQuery.data.questions.map((q, idx) => (
            <div key={q.kbQuizQuestionId} className="rounded-md border border-slate-200 bg-white p-3">
              <div className="text-sm font-medium text-slate-900">
                {idx + 1}. {q.prompt}
              </div>
              <div className="mt-2 space-y-1">
                {q.choices.map((c) => (
                  <label
                    key={c.kbQuizChoiceId}
                    className="flex items-start gap-2 rounded px-1 py-1 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <input
                      type={q.questionType === 'multi' ? 'checkbox' : 'radio'}
                      name={`q-${q.kbQuizQuestionId}`}
                      checked={answers[q.kbQuizQuestionId]?.includes(c.kbQuizChoiceId) ?? false}
                      onChange={() => toggleAnswer(q.kbQuizQuestionId, c.kbQuizChoiceId, q.questionType === 'multi')}
                      className="mt-0.5"
                    />
                    <span>{c.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <Button
            disabled={!allAnswered}
            onClick={() => submitMutation.mutate()}
            loading={submitMutation.isPending}
          >
            Submit Answers
          </Button>
        </div>
      )}

      {result && (
        <div className="mt-3 rounded-md border bg-white p-3">
          <div className={`text-sm font-semibold ${result.passed ? 'text-emerald-600' : 'text-red-600'}`}>
            {result.passed ? 'Passed' : 'Did not pass'} — {result.scorePct}%
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {result.passed
              ? 'This training has been marked complete on your assignment.'
              : `You need ${result.passThreshold}% to pass. Review the article and try again.`}
          </p>
          {!result.passed && (
            <Button
              className="mt-2"
              variant="secondary"
              onClick={() => {
                setAttemptId(null);
                setAnswers({});
                setResult(null);
              }}
            >
              Try Again
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
